import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/Checkbox'
import { Button } from '@/components/ui/button'

const facultyFormSchema = z.object({
  name: z.string().min(1, 'Faculty name is required'),
  description: z.string().optional(),
  isActive: z.boolean(),
})

export type FacultyFormValues = z.infer<typeof facultyFormSchema>

interface FacultyFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValues?: FacultyFormValues
  submitting?: boolean
  errorMessage?: string | null
  onSubmit: (values: FacultyFormValues) => Promise<void> | void
  onClose: () => void
}

export const FacultyFormDialog: React.FC<FacultyFormDialogProps> = ({
  open,
  mode,
  initialValues,
  submitting = false,
  errorMessage,
  onSubmit,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FacultyFormValues>({
    resolver: zodResolver(facultyFormSchema),
    defaultValues: initialValues ?? {
      name: '',
      description: '',
      isActive: true,
    },
  })

  useEffect(() => {
    if (open) {
      reset(
        initialValues ?? {
          name: '',
          description: '',
          isActive: true,
        },
      )
    }
  }, [open, initialValues, reset])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={mode === 'create' ? 'Create Faculty' : 'Edit Faculty'}
      size="md"
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Faculty Name *
          </label>
          <Input
            placeholder="Faculty of Business"
            {...register('name')}
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Description
          </label>
          <textarea
            rows={3}
            className="w-full rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            placeholder="Describe the faculty scope and focus"
            {...register('description')}
          />
        </div>

        <Checkbox label="Faculty is active" {...register('isActive')} />

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : mode === 'create' ? 'Create Faculty' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
